import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { PrimaryButton } from "../components/ui";
import { JurisdictionField } from "../components/JurisdictionField";
import { IconCheck, IconCopy } from "../components/icons";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ProjectsRow } from "../components/OverviewTopbar";
import {
  api,
  ApiError,
  isProjectVerified,
  isTargetVerified,
  type ScopeTarget,
} from "../api";
import { isValidDomain, toAsciiDomain } from "../domain";
import { demoStore } from "../demo/demoStore";
import { useDemo } from "../demo/useDemo";
import { DEMO_HOST } from "../demo/demoData";
import { jurisdictionCreateCode } from "../jurisdiction";
import { useJurisdictions } from "../useJurisdictions";
import { useAppState } from "../AppStateContext";
import { useI18n } from "../i18n";
import "./overview.css";

type DnsInstructions = {
  type?: string;
  name?: string;
  value?: string;
  ttl?: number;
};

// How long the "Domain verified" line stays up before we navigate away. Without
// it the success state lasted exactly as long as two network calls and the user
// never saw confirmation that anything worked.
const SUCCESS_DWELL_MS = 1600;

type OnboardingPhase =
  | "idle"
  | "registering"
  | "awaiting_dns"
  | "checking"
  | "verified"
  | "failed";

export default function OnboardingPage() {
  const nav = useNavigate();
  const {
    projects,
    activeProject,
    createProject,
    deleteProject,
    markProjectVerified,
    reload,
    noPlan,
  } = useAppState();

  // Pick post-onboarding destination by checking subscription fresh, because
  // `noPlan` in this closure may be stale right after `await reload()`.
  const destinationAfterOnboarding = async (): Promise<string> => {
    try {
      const sub = await api.subscription();
      return sub.plan ? "/overview" : "/settings";
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.status === 412 || err.code === "FAILED_PRECONDITION")
      ) {
        return "/settings";
      }
      return "/overview";
    }
  };
  const { t } = useI18n();
  const { options: jurisdictions } = useJurisdictions();
  // The product tour pre-fills the sample host so the user can go straight to
  // Register (they may still type their own — every fixture takes the host).
  const [host, setHost] = useState(() => (demoStore.active ? DEMO_HOST : ""));
  // The demo can turn on while this page is already mounted (the welcome card
  // runs before Start tour), so the initializer above misses it — fill the host
  // when the demo activates and the field is still empty.
  const demo = useDemo();
  const [jurisdiction, setJurisdiction] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; label: string } | null>(null);
  const [dns, setDns] = useState<DnsInstructions | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [target, setTarget] = useState<ScopeTarget | null>(null);
  const [phase, setPhase] = useState<OnboardingPhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [creatingFresh, setCreatingFresh] = useState(false);
  const [hostTouched, setHostTouched] = useState(false);
  // Synchronous re-entrancy lock: `phase` is React state, so the `busy`-based
  // `disabled` doesn't update before a second trigger (Enter key + button
  // click, or a double-click) can fire registerHost again — that double-fired
  // createProject(), leaving one real project and one empty orphan. A ref flips
  // synchronously, so the second call bails before the first yields at `await`.
  const registeringRef = useRef(false);

  useEffect(() => {
    if (!demo.active) return;
    const id = window.setTimeout(() => setHost((h) => h || DEMO_HOST), 0);
    return () => window.clearTimeout(id);
  }, [demo.active]);

  const busy = phase === "registering" || phase === "checking" || phase === "verified";
  const hostInvalid = host.trim() !== "" && !isValidDomain(host);
  const inlineHostError = hostTouched && hostInvalid ? t("onboarding.siteInvalid") : null;

  // Skip onboarding only when there's nothing left to verify — i.e. every
  // existing project is verified. If the user has an unverified project
  // (just created or not finished verification) we stay here so they can
  // complete it.
  useEffect(() => {
    if (projects.length === 0) return;
    // After a successful check verifyTarget owns the navigation — it holds the
    // success message up for a moment first.
    if (phase === "verified") return;
    const hasUnverified = projects.some((p) => !isProjectVerified(p));
    if (!hasUnverified) {
      // No plan yet → send user to plan picker instead of an empty overview.
      nav(noPlan ? "/settings" : "/overview", { replace: true });
    }
  }, [projects, nav, noPlan, phase]);

  // If the project we were verifying disappears from the list (deleted via
  // the trash button), reset local state so the form is ready for a fresh
  // attempt.
  useEffect(() => {
    if (!projectId) return;
    if (projects.some((p) => p.project_id === projectId)) return;
    setProjectId(null);
    setTarget(null);
    setHost("");
    setJurisdiction("");
    setPhase("idle");
    setError(null);
    setHostTouched(false);
  }, [projectId, projects]);

  // Auto-load the project that should be onboarded. Prefer the globally
  // selected project if it's unverified (so picking an unverified project
  // from the PROJECTS row routes us here with the right form preloaded);
  // otherwise fall back to the most recent unverified project so a fresh
  // tab can pick up an in-progress verification.
  useEffect(() => {
    if (creatingFresh) return;
    if (projects.length === 0) return;
    // Don't swap the form out from under a check in flight: reload() can bring
    // in another unverified project, and repointing the form at it would wipe
    // the success message the user is meant to see.
    if (phase === "checking" || phase === "verified") return;
    const candidate =
      activeProject && !isProjectVerified(activeProject)
        ? activeProject
        : [...projects]
            .filter((p) => !isProjectVerified(p))
            .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))[0];
    if (!candidate) return;
    if (candidate.project_id === projectId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { targets } = await api.listScopeTargets(candidate.project_id);
        const tgt = targets[0];
        if (!tgt || cancelled) return;
        setProjectId(candidate.project_id);
        setHost(tgt.target);
        setTarget(tgt);
        if (tgt.verification_token || tgt.verification_instruction) {
          setDns(parseDnsInstruction(tgt.verification_instruction, tgt.verification_token));
          setPhase("awaiting_dns");
        } else {
          try {
            const after = await api.startVerifyTarget(tgt.target_id, "dns");
            if (cancelled) return;
            setTarget(after);
            setDns(parseDnsInstruction(after.verification_instruction, after.verification_token));
          } catch {
            /* leave dns null; user can retry via button */
          }
          setPhase("awaiting_dns");
        }
      } catch {
        /* keep idle, user can register fresh */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projects, projectId, activeProject, creatingFresh, phase]);

  const registerHost = async () => {
    // Already creating a project — ignore the duplicate trigger (see ref above).
    if (registeringRef.current) return;
    const cleaned = host
      .trim()
      .replace(/^https?:\/\//, "")
      .replace(/\/$/, "");
    if (!cleaned) {
      setError(t("onboarding.siteRequired"));
      return;
    }
    if (!isValidDomain(cleaned)) {
      setError(t("onboarding.siteInvalid"));
      return;
    }
    // Backend's scope/targets validator rejects raw IDN (e.g. "мойсайт.рф")
    // with 422. The URL parser punycodes it for us → "xn--80a...xn--p1ai".
    const asciiDomain = toAsciiDomain(cleaned);
    registeringRef.current = true;
    setPhase("registering");
    setError(null);
    try {
      const jurisdictionCode = jurisdictionCreateCode({
        selected: jurisdiction,
        enabledCodes: jurisdictions.map((j) => j.code),
      });
      let project;
      try {
        project = await createProject({
          name: cleaned,
          description: cleaned,
          jurisdiction_code: jurisdictionCode,
        });
        setCreatingFresh(false);
      } catch (err) {
        setPhase("idle");
        setError(err instanceof Error ? err.message : t("onboarding.createFailed"));
        return;
      }
      let created;
      try {
        created = await api.addScopeTarget(project.project_id, asciiDomain);
      } catch (err) {
        // Target insert failed (e.g. 422 on a malformed host). Don't leave the
        // user staring at a spinner in step 2 — surface the error on step 1
        // and roll back the empty project so retries don't pile up orphans.
        try {
          await deleteProject(project.project_id);
        } catch {
          /* best-effort cleanup; ignore */
        }
        setPhase("idle");
        setError(err instanceof Error ? err.message : t("onboarding.createFailed"));
        return;
      }
      setProjectId(project.project_id);
      setTarget(created);
      try {
        const after = await api.startVerifyTarget(created.target_id, "dns");
        setTarget(after);
        setDns(parseDnsInstruction(after.verification_instruction, after.verification_token));
        setPhase("awaiting_dns");
      } catch (err) {
        // startVerify hiccuped — render whatever hints the addScopeTarget
        // response already carried so step 2 isn't stuck on a spinner.
        if (created.verification_token || created.verification_instruction) {
          setDns(parseDnsInstruction(created.verification_instruction, created.verification_token));
        }
        setPhase("awaiting_dns");
        setError(err instanceof Error ? err.message : t("onboarding.registerFailed"));
      }
    } finally {
      registeringRef.current = false;
    }
  };

  const verifyTarget = async () => {
    if (!target) {
      await registerHost();
      return;
    }
    setPhase("checking");
    setError(null);
    try {
      const result = await api.completeVerifyTarget(target.target_id);
      if (!result.verified) {
        setPhase("failed");
        // Keep the backend's raw (English) reason out of the UI — surface a
        // single localized message; log the detail for debugging.
        if (result.failure_reason) console.warn("target verify failed:", result.failure_reason);
        setError(t("onboarding.dnsCantFind"));
        return;
      }
      // Target-level truth. The project's verified_targets counter is updated
      // asynchronously on the backend, so the reload below can still report the
      // project as unverified — which used to bounce the user straight back
      // into this form. Trust the verified target the API just handed us.
      setTarget(result.target);
      setPhase("verified");
      const verifiedAt = Date.now();
      let destination = "/overview";
      try {
        if (projectId && (await projectFullyVerified(projectId, result.target))) {
          markProjectVerified(projectId);
        }
        await reload();
        destination = await destinationAfterOnboarding();
      } catch {
        // The check itself passed — a hiccup while refreshing state must not be
        // reported back as a failed verification. Take the default route.
      }
      // Keep the success line on screen long enough to be read — the two calls
      // above already ate part of the window.
      const elapsed = Date.now() - verifiedAt;
      if (elapsed < SUCCESS_DWELL_MS) {
        await new Promise((resolve) => setTimeout(resolve, SUCCESS_DWELL_MS - elapsed));
      }
      nav(destination, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setProjectId(null);
        setTarget(null);
        setPhase("idle");
        await reload();
        setError(t("onboarding.sessionExpired"));
        return;
      }
      // Backend says verification was never started (no token issued yet) —
      // start it now, refresh the DNS hints shown to the user, and ask them
      // to re-check after they've added the new record. Avoids a dead-end
      // state where "Проверить" can only fail.
      if (
        err instanceof ApiError &&
        (err.code === "FAILED_PRECONDITION" || err.status === 412) &&
        /verification has not been started/i.test(err.message ?? "")
      ) {
        try {
          const started = await api.startVerifyTarget(target.target_id, "dns");
          setTarget(started);
          setDns(parseDnsInstruction(started.verification_instruction, started.verification_token));
          setPhase("awaiting_dns");
          setError(t("onboarding.verificationRestarted"));
          return;
        } catch (innerErr) {
          setPhase("failed");
          setError(innerErr instanceof Error ? innerErr.message : t("onboarding.verificationFailed"));
          return;
        }
      }
      setPhase("failed");
      setError(err instanceof Error ? err.message : t("onboarding.verificationFailed"));
    }
  };

  const handleConfirm = () => {
    if (!projectId) void registerHost();
    else void verifyTarget();
  };

  const buttonLabel = () => {
    if (phase === "registering") return t("onboarding.registering");
    if (phase === "checking") return t("onboarding.checking");
    if (phase === "verified") return t("onboarding.verified");
    if (!projectId) return t("onboarding.registerSite");
    if (phase === "failed") return t("onboarding.checkAgain");
    return t("onboarding.iAddedRecord");
  };

  const showProjectsRow = projects.length > 0;
  return (
    <AppShell>
      <div className="onboarding anim-page">
        {showProjectsRow ? (
          <ProjectsRow
            searches={projects.map((p) => ({
              id: p.project_id,
              label: p.name,
              verified: isProjectVerified(p),
            }))}
            activeId={projectId ?? undefined}
            onNew={() => {
              setCreatingFresh(true);
              setProjectId(null);
              setTarget(null);
              setHost("");
              setPhase("idle");
              setError(null);
              setHostTouched(false);
            }}
            onDelete={(id, label) => setConfirmDelete({ id, label })}
          />
        ) : null}
        <h1 className="onboarding__title">
          {showProjectsRow ? t("onboarding.titleVerify") : t("onboarding.titleFirst")}
        </h1>
        {showProjectsRow ? (
          <p className="onboarding__subtitle">{t("onboarding.subtitleVerifyExisting")}</p>
        ) : null}

        <section className="onboarding__step">
          <div className="onboarding__num">1</div>
          <div className="card card--lg">
            <p className="card__title">{t("onboarding.step1Title")}</p>
            <div className="tf tf--focus tf--inline">
              <span className="tf__lead">https://</span>
              <input
                className="tf__input"
                data-tour="site-host"
                placeholder={t("onboarding.step1Placeholder")}
                value={host}
                autoFocus
                disabled={Boolean(projectId)}
                // Strip a pasted/typed protocol immediately (the field already
                // shows a fixed `https://` lead) — don't wait for a reload.
                onChange={(e) => setHost(e.target.value.replace(/^\s*https?:\/\//i, ""))}
                onBlur={() => setHostTouched(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && host && !projectId) {
                    e.preventDefault();
                    setHostTouched(true);
                    void registerHost();
                  }
                }}
                aria-invalid={hostInvalid ? true : undefined}
              />
              {projectId ? (
                <span className="tf__badge" title={t("onboarding.projectRegisteredTitle")}>
                  <IconCheck width={16} height={16} />
                </span>
              ) : null}
            </div>
            {!projectId ? (
              <>
                {jurisdictions.length > 0 ? (
                  <JurisdictionField
                    options={jurisdictions}
                    value={jurisdiction}
                    onChange={setJurisdiction}
                    placeholder={t("jurisdiction.placeholder")}
                  />
                ) : null}
                {inlineHostError ? (
                  <p className="auth-error">{inlineHostError}</p>
                ) : error && phase === "idle" ? (
                  <p className="auth-error">{error}</p>
                ) : null}
                <div className="onboarding__actions">
                  <PrimaryButton
                    className="btn--auto"
                    data-tour="register-site"
                    onClick={() => {
                      setHostTouched(true);
                      void registerHost();
                    }}
                    disabled={busy || !host.trim() || !isValidDomain(host)}
                  >
                    {phase === "registering" ? (
                      <span className="onboarding__btn-busy">
                        <span className="onboarding__spinner" aria-hidden />
                        {t("onboarding.registering")}
                      </span>
                    ) : (
                      t("onboarding.registerSite")
                    )}
                  </PrimaryButton>
                </div>
              </>
            ) : null}
          </div>
        </section>

        {projectId ? (
        <section className="onboarding__step onboarding__step--reveal">
          <div className="onboarding__num">2</div>
          <div className="card card--lg" data-tour="dns-card">
            <header className="card__head">
              <p className="card__title">{t("onboarding.step2Title")}</p>
              <a
                className="card__link"
                href={t("onboarding.whereIsItUrl")}
                target="_blank"
                rel="noreferrer"
              >
                {t("onboarding.whereIsIt")}
              </a>
            </header>
            {dns ? (
              <>
                <DnsRow label={t("onboarding.dnsType")} value={dns.type ?? "TXT"} />
                <DnsRow label={t("onboarding.dnsName")} value={dns.name ?? "@"} copyable />
                <DnsRow label={t("onboarding.dnsValue")} value={dns.value ?? ""} copyable />
                <DnsRow
                  label="TTL"
                  value={dns.ttl ? t("onboarding.ttlMin", { minutes: dns.ttl / 60 }) : t("onboarding.ttlHour")}
                />
              </>
            ) : (
              <div className="onboarding__dns-loading">
                <span className="onboarding__spinner" aria-hidden />
              </div>
            )}
            {phase === "verified" ? (
              <p className="onboarding__success">
                <IconCheck width={16} height={16} />
                {t("onboarding.domainVerified")}
              </p>
            ) : null}
            {error && phase !== "idle" ? <p className="auth-error">{error}</p> : null}
            <div className="onboarding__actions">
              <PrimaryButton
                className="btn--auto"
                data-tour="verify-site"
                onClick={handleConfirm}
                disabled={busy || !dns}
              >
                {phase === "checking" ? (
                  <span className="onboarding__btn-busy">
                    <span className="onboarding__spinner" aria-hidden />
                    {buttonLabel()}
                  </span>
                ) : (
                  buttonLabel()
                )}
              </PrimaryButton>
            </div>
          </div>
        </section>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirmDelete !== null}
        title={t("onboarding.confirmDeleteTitle")}
        body={t("onboarding.confirmDeleteBody", { name: confirmDelete?.label ?? "" })}
        confirmLabel={t("onboarding.confirmDeleteLabel")}
        cancelLabel={t("common.cancel")}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await deleteProject(confirmDelete.id);
          } catch (err) {
            setError(err instanceof Error ? err.message : t("onboarding.deleteFailed"));
          } finally {
            setConfirmDelete(null);
          }
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </AppShell>
  );
}

/** Are all of the project's targets verified? Asked of the targets themselves,
 * since the project's counters lag. `justVerified` is the target the complete
 * call returned — the listing may not know about it yet. If the listing can't
 * be fetched we trust the one verification we did observe: this flow registers
 * a single target per project. */
async function projectFullyVerified(
  projectId: string,
  justVerified: ScopeTarget,
): Promise<boolean> {
  try {
    const { targets } = await api.listScopeTargets(projectId);
    return targets.every(
      (t) => t.target_id === justVerified.target_id || isTargetVerified(t),
    );
  } catch {
    return true;
  }
}

function parseDnsInstruction(
  raw: string | null,
  token: string | null,
): DnsInstructions | null {
  // Spec returns instruction as JSON in some flows, plain English in others.
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<DnsInstructions>;
      const value = parsed.value ?? token;
      if (value) {
        return {
          type: parsed.type ?? "TXT",
          name: parsed.name ?? "@",
          value,
          ttl: parsed.ttl ?? 3600,
        };
      }
    } catch {
      /* parse below */
    }
    const m = /TXT\s+record\s+(\S+?)(?:\s+with\s+value\s+"?([^"]+)"?)?\.?$/i.exec(raw);
    if (m && (m[2] || token)) {
      return { type: "TXT", name: m[1], value: m[2] ?? token!, ttl: 3600 };
    }
  }
  if (token) {
    return { type: "TXT", name: "@", value: token, ttl: 3600 };
  }
  return null;
}

function DnsRow({
  label,
  value,
  copyable,
}: {
  label: string;
  value: string;
  copyable?: boolean;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* fallback: select */
    }
  };
  return (
    <div className="dns-row">
      <span className="dns-row__label">{label}</span>
      <div className="dns-row__value">
        <span>{value}</span>
        {copyable ? (
          <button type="button" aria-label={t("common.copy")} onClick={onCopy}>
            {copied ? <IconCheck width={20} height={20} /> : <IconCopy width={20} height={20} />}
          </button>
        ) : null}
      </div>
    </div>
  );
}
